import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const CircleGraph = ({ data }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;
    
    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();
    
    // Swap width and height for horizontal layout
    const width = 1000; // Increased width for horizontal layout
    const height = 500;  // Reduced height
    
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    // Add zoomable container with initial transform
    const zoomContainer = svg.append('g')
      .attr('transform', `translate(50, ${height/2})`); // Center vertically

    svg.call(
      d3.zoom()
        .extent([[0, 0], [width, height]])
        .scaleExtent([0.5, 5])
        .on('zoom', (event) => {
          zoomContainer.attr('transform', event.transform);
        })
    );

    // Create gradient definitions (keeping existing gradients)
    const defs = svg.append('defs');
    
    const gradients = {
      'SEO': ['#64B5F6', '#1E88E5'],
      'Email Marketing': ['#81C784', '#43A047'],
      'Strategy': ['#9575CD', '#5E35B1'],
      'Marketplace': ['#FFB74D', '#FB8C00'],
      'Content': ['#4DD0E1', '#00ACC1'],
      'Social': ['#F48FB1', '#E91E63']
    };

    Object.entries(gradients).forEach(([key, [color1, color2]]) => {
      const gradient = defs.append('radialGradient')
        .attr('id', `gradient-${key.toLowerCase().replace(/\s+/g, '-')}`)
        .attr('cx', '50%')
        .attr('cy', '50%')
        .attr('r', '50%')
        .attr('fx', '50%')
        .attr('fy', '50%');

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', color1)
        .attr('stop-opacity', 0.9);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', color2)
        .attr('stop-opacity', 0.7);
    });

    // Create pack layout optimized for horizontal layout
    const pack = d3.pack()
      .size([height - 100, width - 200]) // Swap dimensions for horizontal layout
      .padding(50); // Fixed padding for parent circles

    // Process the data
    const root = d3.hierarchy(data)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    const nodes = pack(root);

    // Transform the nodes for horizontal layout
    nodes.descendants().forEach(node => {
      // Swap x and y coordinates
      const tempX = node.x;
      node.x = node.y;
      node.y = tempX;
    });

    // Create groups for each node
    const node = zoomContainer.selectAll('g')
      .data(nodes.descendants())
      .enter()
      .append('g')
      .attr('transform', d => `translate(${d.x},${d.y - height/2})`);

    // Add parent circles with fixed distance
    node.filter(d => d.depth === 1)
      .append('circle')
      .attr('r', 0)
      .attr('fill', d => `url(#gradient-${d.data.name.toLowerCase().replace(/\s+/g, '-')})`)
      .attr('fill-opacity', 0.15)
      .attr('stroke', d => gradients[d.data.name][1])
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.5)
      .transition()
      .duration(1000)
      .attr('r', 50); // Fixed radius for parent circles

    // Add child circles
    node.filter(d => d.depth === 2)
      .append('circle')
      .attr('r', 0)
      .attr('fill', d => `url(#gradient-${d.parent.data.name.toLowerCase().replace(/\s+/g, '-')})`)
      .attr('fill-opacity', 0.8)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5)
      .transition()
      .duration(1000)
      .delay(500)
      .attr('r', d => d.r * 1.2);

    // Add hover effects
    node.selectAll('circle')
      .on('mouseover', function() {
        d3.select(this)
          .transition()
          .duration(300)
          .attr('stroke-width', 3)
          .attr('stroke-opacity', 1);
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .transition()
          .duration(300)
          .attr('stroke-width', d.depth === 1 ? 2 : 1.5)
          .attr('stroke-opacity', d.depth === 1 ? 0.5 : 1);
      });

    // Function to wrap text
    const wrapText = (text, width) => {
      const words = text.split(/\s+/);
      if (words.length === 1) return [text];
      
      const lines = [];
      let line = [];
      let lineWidth = 0;
      const spaceWidth = 4;
      
      words.forEach(word => {
        const wordWidth = word.length * 6;
        if (lineWidth + wordWidth + spaceWidth <= width) {
          line.push(word);
          lineWidth += wordWidth + spaceWidth;
        } else {
          lines.push(line.join(' '));
          line = [word];
          lineWidth = wordWidth;
        }
      });
      
      if (line.length > 0) {
        lines.push(line.join(' '));
      }
      
      return lines;
    };

    // Add parent labels with text wrapping
    node.filter(d => d.depth === 1)
      .each(function(d) {
        const label = d3.select(this).append('g');
        
        const lines = wrapText(d.data.name, 50); // Fixed width for wrapping
        lines.forEach((line, i) => {
          label.append('text')
            .attr('dy', (i - lines.length/2) * 20)
            .attr('text-anchor', 'middle')
            .attr('fill', gradients[d.data.name][1])
            .style('font-family', 'Inter, sans-serif')
            .style('font-size', '14px') // Fixed font size
            .style('font-weight', '600')
            .style('pointer-events', 'none')
            .text(line);
        });

        label.append('text')
          .attr('dy', (lines.length/2 + 1) * 20)
          .attr('text-anchor', 'middle')
          .attr('fill', gradients[d.data.name][1])
          .style('font-family', 'Inter, sans-serif')
          .style('font-size', '12px') // Fixed font size
          .style('font-weight', '500')
          .text(d.value);
      });

    // Add child labels
    node.filter(d => d.depth === 2)
      .append('text')
      .attr('dy', 0)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff')
      .style('font-family', 'Inter, sans-serif')
      .style('font-size', d => `${Math.min(d.r / 2.5, 12)}px`)
      .style('font-weight', '500')
      .style('pointer-events', 'none')
      .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
      .text(d => d.data.name);

    // Add child value labels
    node.filter(d => d.depth === 2)
      .append('text')
      .attr('dy', '1.2em')
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff')
      .style('font-family', 'Inter, sans-serif')
      .style('font-size', d => `${Math.min(d.r / 3, 10)}px`)
      .style('font-weight', '400')
      .style('pointer-events', 'none')
      .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
      .text(d => d.value);

    // Add header
    svg.append('g')
      .attr('transform', 'translate(20, 25)')
      .append('text')
      .attr('class', 'header')
      .attr('fill', '#333')
      .style('font-family', 'Inter, sans-serif')
      .style('font-size', '14px')
      .style('font-weight', '500')
      .text('Task · Overview');

  }, [data]);

  return (
    <div style={styles.container}>
      <svg ref={svgRef} style={styles.svg}></svg>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  svg: {
    width: '100%',
    height: '100%',
    display: 'block',
  }
};

export default CircleGraph;